<script setup>
const certificatesAvailable = [
  {
    id: 'vue',
    title: 'Vue.js',
    released: true,
    iconClass: 'h-10',
    description: 'The official certification of competence for the <span class="bg-gradient-to-r from-mint to-cerulean bg-clip-text text-transparent">Vue.js framework.</span>',
    preCreator: 'in partnership with',
    creator: {
      name: 'Evan You',
      role: 'Creator of Vue.js',
      avatar: '/images/evan1.png',
      avatarClass: 'size-20 rounded-full object-cover'
    },
    subtitle: 'Built with reviews from the Creator of Vue, Evan You, and interested members of the Vue.js Core Team. <br> <br> Bringing together some of the greatest Vue.js experts to make this the best product that will adequately test the competencies that are necessary to achieve the most effective use of the Vue.js framework!',
    tags: [
      'Partnered with Vue.js',
      'Senior Certificate Available',
      'Preparation Guides',
      'Self-Study Training',
      'Boot Camps',
      'Expert Examiners'
    ],
    background: 'bg-gradient-to-bl from-[#42B883]/50 to-cerulean/50',
    cta: {
      text: ' Get Vue.js Certified',
      link: '/vuejs'
    }

  },
  {
    id: 'javascript',
    title: 'JavaScript',
    released: true,
    iconClass: 'h-10',
    description: 'Get your <span class="text-[#F7DF1E]">JavaScript</span><br>development skills<br>certified',
    heading: 'Get your JavaScript development skills certified',
    subtitle: `Validate your expertise in modern JavaScript through our rigorous online assessments, designed by top industry experts.

    Come out ahead for new positions and promotions with two levels of certification designed for Developers and those aspiring to be recognized as Senior Developers.`,
    tags: [
      'javascript',
      'Senior Certificate Available',
      'Preparation Guides',
      'Self-Study Training',
      'Boot Camps',
      'Expert Examiners'
    ],
    background: 'bg-[#F7DF1E]/20',
    cta: {
      text: ' Get JavaScript Certified',
      link: '/javascript'
    }
  },
  {
    id: 'angular',
    title: 'Angular',
    released: true,
    iconClass: 'h-12 w-auto',
    description: 'Earn your <span class="text-[#F921BA]">certification of competence</span> for the Angular framework.',
    heading: 'Get your Angular Development skills certified.',
    subtitle: 'Get your Angular Development skills certified. Our rigorous online examinations will thoroughly test your Angular development skills. <br> <br>  Get certified with confidence. Developed, reviewed, and updated by Google Developers Experts, this certification validates your proficiency in this popular framework, showcasing your expertise to potential employers. <br><br> Three levels of certification will be made available:<ul class="list-disc pl-6 mt-2"><li>Certified Junior Angular Developer</li><li>Certified Mid-Level Angular Developer</li><li>Certified Senior Angular Developer</li></ul>',
    background: 'bg-gradient-to-br from-still-red/30 to-dreem/30',
    cta: {
      text: 'Get Angular Certified',
      link: '/angular'
    },
    preCreator: 'In partnership with Angular Training and their team of Google Developer Experts',
    creator: {
      avatar: '/images/angular-partners.svg',
      avatarDark: '/images/angular-partners.svg',
      avatarClass: 'h-12 w-auto'
    }

  },
  {
    id: 'nuxt',
    title: 'Nuxt',
    released: true,
    iconClass: 'h-10',
    description: 'The official certification of competence for the <span class="text-[#00DC82]">Nuxt framework</span>.',
    preCreator: 'in partnership with',
    creator: {
      name: 'Sébastien Chopin',
      role: 'Creator of Nuxt',
      avatar: '/images/sebastien.png',
      avatarClass: 'size-20 rounded-full object-cover'
    },
    heading: 'Validate Your Nuxt Expertise with the Official Certification ',
    subtitle: 'Developed in collaboration with Nuxt creator, Sébastien Chopin, this certification will assess the skills and best practices needed to be a Nuxt developer. Earn your certification and demonstrate your knowledge of core functionalities like routing, server-side rendering, and code splitting. <br><br> Our in-depth certification program offers two progressive levels: Nuxt Developer and Senior Nuxt Developer.',
    tags: [
      'Partnered with Nuxt.com',
      'Senior Certificate Available',
      'Preparation Guides',
      'Self-Study Training',
      'Boot Camps',
      'Expert Examiners'
    ],
    background: 'bg-[#00DC82]/20',
    cta: {
      text: ' Get Nuxt Certified',
      link: '/nuxt'
    }

  },
  {
    id: 'react',
    title: 'React',
    released: true,
    iconClass: 'h-10',
    description: 'Validate Your <em class="text-react-blue">React</em> Expertise<br>Junior, Mid-Level and Senior',
    heading: 'Validate Your React Expertise',
    subtitle: 'Demonstrate your React skills by taking our comprehensive exams curated by the best industry professionals. Take the next step in your career with a certificate for a React Developer and build up to a Senior React Developer distinction.',
    background: 'bg-react-blue/20',
    preCreator: 'in partnership with',
    creator: {
      name: 'Aurora Scharff',
      role: 'React Expert & Microsoft MVP in Web Technologies',
      avatar: '/images/board-aurora.jpg',
      avatarClass: 'size-20 rounded-full object-cover'
    },
    tags: [
      'React',
      'Preparation Guides',
      'Self-Study Training',
      'Boot Camps',
      'Expert Examiners'
    ],
    cta: {
      text: 'Get React Certified',
      link: '/react'
    }
  },
  {
    id: 'typescript',
    title: 'TypeScript',
    released: true,
    iconClass: 'h-10',
    description: 'Get your <em class="text-ts-blue">TypeScript</em> skills certified',
    heading: 'Get your TypeScript skills certified',
    tags: [
      'TypeScript',
      'Preparation Guides',
      'Self-Study Training',
      'Boot Camps',
      'Expert Examiners'
    ],
    subtitle: 'Prove your skills in TypeScript with our highly professional assessments from top experts in the field. Get certified as a TypeScript Developer or get certified in Senior TypeScript Developer through new career paths.',
    background: 'bg-ts-blue/20',
    cta: {
      text: 'Get early access',
      link: '/typescript'
    }

  }
]

const certificationsReleased = computed(() => {
  return certificatesAvailable.filter(certificate => certificate.released)
})

const certificationsToBeReleased = computed(() => {
  return certificatesAvailable.filter(certificate => !certificate.released).reverse()
})
</script>

<template>
  <AppContainer
    id="available-certificates"
    class="pt-32 md:pt-40">
    <h2 class="heading-primary mx-auto text-center text-3xl font-bold md:text-[52px] md:leading-tight">
      Available  <em>Developer Certifications</em>
    </h2>

    <div
      v-for="certificate in certificationsReleased"
      :key="certificate.id"
      class="mt-10 flex h-full flex-col gap-10 rounded-2xl bg-eerie-black/5 p-6 md:mt-20 md:p-10 xl:flex-row dark:bg-dark-gunmetal">
      <div class="order-2 flex flex-col items-center gap-8 md:items-start xl:w-3/5">
        <div class="flex w-full flex-row items-start justify-between gap-4">
          <div class="flex items-center gap-8">
            <InlineSvg
              :src="`/images/homepage/tech-${certificate.id}.svg`"
              :class="certificate.iconClass"
              :alt="`${certificate.id} Logo`" />
            <h2 class="heading-primary text-3xl font-bold md:text-[40px] md:leading-tight">
              {{ certificate.title }}
            </h2>
          </div>
          <p
            v-if="!certificate.released"
            class="w-fit rounded-md bg-dodger-shine/20 px-4 py-2 text-xs font-semibold uppercase tracking-[1.3px] text-dodger-shine">
            releases soon
          </p>
        </div>
        <div class="flex flex-1 flex-col items-center gap-8  md:items-start">
          <h3
            v-if="certificate?.heading"
            class="text-center  font-bold md:text-left md:text-lg"
            v-html="certificate.heading" />
          <div
            class="flex-1 text-center text-sm text-eerie-black md:text-left md:text-base dark:text-white"
            v-html="certificate.subtitle" />
          <div class="flex flex-wrap justify-center gap-2 md:justify-start">
            <span
              v-for="tag in certificate.tags"
              :key="tag"
              class="h-fit rounded-md bg-eerie-black/10 px-[8px] py-[10px] text-[10px] font-bold uppercase tracking-wide text-body-gray  dark:bg-eerie-black/50">
              {{ tag }}
            </span>
          </div>
          <NuxtLink
            v-if="certificate.cta"
            :href="certificate.cta?.link"
            class="btn-primary w-full px-8 py-4 md:w-fit"
            target="_blank">
            {{ certificate.cta.text }}
          </NuxtLink>
        </div>
      </div>
      <div
        class="order-1 flex min-h-full flex-1 flex-col items-center justify-center gap-10 rounded-2xl p-10 md:flex-row xl:flex-col "
        :class="certificate.background">
        <NuxtImg
          loading="lazy"
          :src="`/images/tech/${certificate.id === 'vue' ? 'vuejs' : certificate.id}/cert-3d.svg`"
          class="mx-auto w-40"
          alt="cert" />
        <div class="flex flex-col items-center gap-6 md:items-start md:gap-2 xl:items-center xl:gap-10">
          <h4
            class="text-center text-2xl font-bold text-eerie-black md:text-left xl:text-center dark:text-light-gray"
            v-html="certificate.description" />
          <div
            v-if="certificate?.preCreator || certificate?.creator"
            class="flex flex-col items-center gap-4">
            <span
              v-if="certificate?.preCreator"
              class="text-center text-eerie-black dark:text-[#E1E1E1]">{{ certificate?.preCreator }}</span>
            <div
              v-if="certificate?.creator"
              class="flex items-center gap-6">
              <NuxtImg
                loading="lazy"
                :src="certificate?.creator?.avatar"
                class="hidden dark:block"
                :class="certificate?.creator?.avatarClass"
                :alt="`Picure of ${certificate?.creator?.name}`" />
              <NuxtImg
                loading="lazy"
                :src="certificate?.creator?.avatarDark ? certificate?.creator?.avatarDark : certificate?.creator?.avatar"
                class="block dark:hidden"
                :class="certificate?.creator?.avatarClass"
                alt="Evan You" />
              <div class="flex flex-col gap-1">
                <p class="text-lg font-bold text-eerie-black dark:text-light-gray">
                  {{ certificate?.creator?.name }}
                </p>
                <span class="text-eerie-black dark:text-white">{{ certificate?.creator?.role }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </AppContainer>
  <AppContainer
    v-if="certificationsToBeReleased.length > 0"
    id="available-certificates"
    class="pt-32 md:pt-40">
    <h2 class="heading-primary mx-auto text-center text-3xl font-bold md:text-[52px] md:leading-tight">
      <em>Certifications</em> In Development
    </h2>

    <div
      v-for="certificate in certificationsToBeReleased"
      :key="certificate.id"
      class="mt-10 flex h-full flex-col gap-10 rounded-2xl bg-eerie-black/5 p-6 md:mt-20 md:p-10 xl:flex-row dark:bg-dark-gunmetal">
      <div class="order-2 flex flex-col items-center gap-8 md:items-start xl:w-3/5">
        <div class="flex w-full flex-row flex-wrap items-center justify-center gap-5 md:items-start md:justify-between md:gap-4">
          <div class="flex items-center gap-8">
            <InlineSvg
              :src="`/images/homepage/tech-${certificate.id}.svg`"
              :class="certificate.iconClass"
              :alt="`${certificate.id} Logo`" />
            <h2 class="heading-primary text-3xl font-bold md:text-[40px] md:leading-tight">
              {{ certificate.title }}
            </h2>
          </div>
          <p
            v-if="!certificate.released"
            class="w-fit rounded-md bg-dodger-shine/20 px-4 py-2 text-xs font-semibold uppercase tracking-[1.3px] text-dodger-shine">
            {{ certificate.id === 'javascript' ? 'launching': 'releases' }} soon
          </p>
        </div>
        <div class="flex flex-col items-center gap-8 md:items-start">
          <h3
            v-if="certificate?.heading"
            class="text-center  font-bold md:text-left md:text-lg"
            v-html="certificate.heading" />
          <p
            class="text-center text-sm text-eerie-black md:text-left md:text-base dark:text-white"
            v-html="certificate.subtitle" />
          <div class="flex flex-wrap justify-center gap-2 md:justify-start">
            <span
              v-for="tag in certificate.tags"
              :key="tag"
              class="rounded-md bg-eerie-black/10 px-[8px] py-[10px] text-[10px] font-bold uppercase tracking-wide text-body-gray dark:bg-eerie-black/50">
              {{ tag }}
            </span>
          </div>
          <NuxtLink
            v-if="certificate.cta"
            :href="certificate.cta?.link"
            class="btn-primary w-full px-8 py-4 md:w-fit"
            target="_blank">
            {{ certificate.cta.text }}
          </NuxtLink>
        </div>
      </div>
      <div
        class="order-1 flex min-h-full flex-1 flex-col items-center justify-around gap-10 rounded-2xl p-10 md:flex-row xl:flex-col "
        :class="certificate.background">
        <NuxtImg
          :src="`/images/tech/${certificate.id === 'vue' ? 'vuejs' : certificate.id}/cert-3d.svg`"
          class="mx-auto w-40"
          loading="lazy"
          alt="cert" />
        <div class="flex flex-col items-center gap-6 md:items-start md:gap-2 xl:items-center xl:gap-10">
          <h4
            class="text-center text-2xl font-bold text-eerie-black md:text-left xl:text-center dark:text-light-gray"
            v-html="certificate.description" />
          <div
            v-if="certificate?.preCreator || certificate?.creator"
            class="flex flex-col items-center gap-4">
            <span
              v-if="certificate?.preCreator"
              class="text-center text-eerie-black dark:text-[#E1E1E1]">{{ certificate?.preCreator }}</span>
            <div
              v-if="certificate?.creator"
              class="flex items-center gap-6">
              <NuxtImg
                loading="lazy"
                :src="certificate?.creator?.avatar"
                class="hidden dark:block"
                :class="certificate?.creator?.avatarClass"
                :alt="`Picure of ${certificate?.creator?.name}`" />
              <NuxtImg
                loading="lazy"
                :src="certificate?.creator?.avatarDark ? certificate?.creator?.avatarDark : certificate?.creator?.avatar"
                class="block dark:hidden"
                :class="certificate?.creator?.avatarClass"
                alt="Evan You" />
              <div class="flex flex-col gap-1">
                <p class="text-lg font-bold text-eerie-black dark:text-light-gray">
                  {{ certificate?.creator?.name }}
                </p>
                <span class="text-eerie-black dark:text-white">{{ certificate?.creator?.role }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </AppContainer>
</template>
